/**
 * Contract Manufacturing (CM) QA — External QC inspection report (third-party inspector).
 * UI aligned with `SellerCmInternalInspectionReportPage` (shared `cm-insp-*` styles).
 * Persisted in qa_inspections.inspection_report with flow marker cm_external_qc.
 */
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaAward,
  FaCamera,
  FaCheck,
  FaDownload,
  FaExclamationCircle,
  FaFileAlt,
  FaFlask,
  FaHome,
  FaInfoCircle,
  FaSave,
  FaTimes,
  FaUpload,
} from 'react-icons/fa';
import CommonService from '../../services/common.service';
import StorageService from '../../services/StorageService';
import { API_URLS } from '../../constants/api-urls';
import '../../assets/styles/seller_cm_internal_inspection_report.scss';

const FLOW_KEY = 'cm_external_qc';

const DEFAULT_CHECKLIST = [
  { id: 'd1', category: 'Visual Inspection', title: 'Surface finish quality', criteria: 'Acceptance per agreed visual standard' },
  { id: 'd2', category: 'Visual Inspection', title: 'Component alignment', criteria: 'Components aligned within tolerance' },
  { id: 'd3', category: 'Visual Inspection', title: 'Color consistency', criteria: 'Uniform color vs reference sample' },
  { id: 'd4', category: 'Dimensional Check', title: 'Length tolerance (+/- 0.5mm)', criteria: 'Measured length within spec' },
  { id: 'd5', category: 'Dimensional Check', title: 'Width tolerance (+/- 0.5mm)', criteria: 'Measured width within spec' },
  { id: 'd6', category: 'Functional Test', title: 'Performance test', criteria: 'Functional criteria per specification' },
  { id: 'd7', category: 'Safety Check', title: 'Electrical safety', criteria: 'Applicable safety requirements' },
  { id: 'd8', category: 'Safety Check', title: 'Material safety', criteria: 'Material compliance requirements' },
];

const COMPLIANCE_STANDARDS = [
  { id: 'iso9001', label: 'ISO 9001:2015', sub: 'Quality Management' },
  { id: 'rohs', label: 'RoHS Compliance', sub: 'Hazardous Substances' },
  { id: 'ce', label: 'CE Marking', sub: 'European Conformity' },
  { id: 'fcc', label: 'FCC Certification', sub: 'Electromagnetic Interference' },
];

const AQL_OPTIONS = [
  { value: '1.0', label: '1.0 (Tight)' },
  { value: '1.5', label: '1.5' },
  { value: '2.5', label: '2.5 (Standard)' },
  { value: '4.0', label: '4.0 (Relaxed)' },
];

/** Same category map as `SellerCmInternalInspectionReportPage` / agreed QC payload */
const CAT_LABEL = {
  visual: 'Visual Inspection',
  dimension: 'Dimensional Check',
  functional: 'Functional Test',
  packaging: 'Packaging / Labeling',
  documentation: 'Documentation',
};

function buildChecklistFromQcPayload(payload) {
  const raw = payload?.inspectionChecklistItems;
  if (!Array.isArray(raw) || raw.length === 0) {
    return DEFAULT_CHECKLIST.map((row) => ({ ...row, status: 'pending', notes: '' }));
  }
  return raw.map((it, idx) => ({
    id: String(it?.id ?? `c-${idx}`),
    category: CAT_LABEL[it?.category] || String(it?.category || 'Inspection'),
    title: String(it?.description || `Check ${idx + 1}`).trim() || `Check ${idx + 1}`,
    criteria: String(it?.acceptanceCriteria || '—').trim() || '—',
    status: 'pending',
    notes: '',
  }));
}

function mergeDraftChecklist(baseRows, draftRows) {
  if (!Array.isArray(draftRows) || draftRows.length === 0) return baseRows;
  const byId = new Map(draftRows.map((r) => [String(r.id), r]));
  return baseRows.map((row) => {
    const d = byId.get(String(row.id));
    if (d && (d.status === 'pass' || d.status === 'fail' || d.status === 'pending')) {
      return {
        ...row,
        status: d.status,
        notes: typeof d.notes === 'string' ? d.notes : row.notes,
      };
    }
    return row;
  });
}

function readQcNormsSampleFields(payload) {
  if (!payload || typeof payload !== 'object') {
    return { aql: '2.5', samplePct: '', minPassPct: '' };
  }
  const aql = typeof payload.aql === 'string' && payload.aql.trim() ? payload.aql.trim() : '2.5';
  const samplePct =
    payload.samplePct != null && String(payload.samplePct).trim() !== '' ? String(payload.samplePct).trim() : '';
  const minPassPct =
    payload.minPassPct != null && String(payload.minPassPct).trim() !== '' ? String(payload.minPassPct).trim() : '';
  return { aql, samplePct, minPassPct };
}

/** External QC docs from agreed QC payload (`externalQcDocs`) — same source as QC modal "External QC Docs" tab. */
function normalizeExternalQcDocs(payload) {
  const raw = payload?.externalQcDocs;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return [...raw]
    .map((d, idx) => ({
      id: String(d?.id ?? `eqc-${idx}`),
      name: String(d?.name || '').trim() || `External QC document ${idx + 1}`,
      fileFormat: String(d?.fileFormat || 'pdf').toLowerCase().replace(/^\./, ''),
      required: d?.required !== false,
      order: typeof d?.order === 'number' ? d.order : idx + 1,
    }))
    .sort((a, b) => a.order - b.order);
}

function acceptForDocFormat(fileFormat) {
  const f = String(fileFormat || 'pdf').toLowerCase();
  if (f === 'pdf') return '.pdf,application/pdf';
  if (f === 'jpg' || f === 'jpeg') return 'image/jpeg';
  if (f === 'png') return 'image/png';
  if (f === 'csv') return '.csv,text/csv';
  if (f === 'xlsx' || f === 'xls') return '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (f === 'doc' || f === 'docx') return '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return '*/*';
}

function formatHintForDoc(doc) {
  const fmt = String(doc.fileFormat || 'pdf').toUpperCase();
  return `Accepted: ${fmt} (max 10MB)`;
}

/** Restore uploaded paths from saved report; migrate legacy fixed `certificates` keys by slot order. */
function hydrateCertificatesFromInner(inner, certificateSlots) {
  const paths = {};
  const names = {};
  const cup = inner?.certificateUploads;
  if (cup && typeof cup === 'object' && !Array.isArray(cup)) {
    Object.entries(cup).forEach(([id, v]) => {
      if (v && typeof v === 'object') {
        const p = v.filePath || v.accessPath || v.path || '';
        const n = v.fileName != null ? String(v.fileName) : '';
        if (p) paths[id] = p;
        if (n) names[id] = n;
      }
    });
    return { paths, names };
  }
  const leg = inner?.certificates;
  if (leg && typeof leg === 'object' && Array.isArray(certificateSlots) && certificateSlots.length > 0) {
    const keys = ['inspection_certificate', 'compliance_certificates', 'lab_test_certificates', 'additional'];
    certificateSlots.forEach((doc, idx) => {
      const k = keys[idx];
      if (k && leg[k]) {
        paths[doc.id] = leg[k];
        names[doc.id] = String(leg[k]).split('/').pop() || 'uploaded';
      }
    });
  }
  return { paths, names };
}

const CERT_ICONS = [FaFileAlt, FaAward, FaFlask, FaUpload];

function checklistToResults(checklist) {
  if (!Array.isArray(checklist) || checklist.length === 0) return null;
  return checklist.map((item, i) => {
    if (typeof item === 'string') {
      return {
        id: `c-${i}`,
        category: 'Inspection',
        title: item,
        criteria: 'Per buyer QA request',
        status: 'pending',
        notes: '',
      };
    }
    return {
      id: item.id || `c-${i}`,
      category: item.category || 'Inspection',
      title: item.name || item.label || item.title || `Item ${i + 1}`,
      criteria: item.criteria || item.acceptance || 'Per buyer requirements',
      status: 'pending',
      notes: item.notes || '',
    };
  });
}

function parseStoredReport(raw) {
  if (!raw) return null;
  const report = typeof raw === 'string' ? JSON.parse(raw) : raw;
  if (report && report[FLOW_KEY]) return report[FLOW_KEY];
  if (report && report.flow === FLOW_KEY) return report;
  return null;
}

function migrateAql(legacy) {
  if (!legacy || typeof legacy !== 'string') return '2.5';
  const m = legacy.match(/(\d+\.?\d*)/);
  return m ? m[1] : '2.5';
}

function migrateComplianceStatus(inner, legacyStandards) {
  const base = COMPLIANCE_STANDARDS.reduce((acc, s) => {
    acc[s.id] = 'pending';
    return acc;
  }, {});
  if (inner?.complianceStatus && typeof inner.complianceStatus === 'object') {
    COMPLIANCE_STANDARDS.forEach((s) => {
      if (inner.complianceStatus[s.id] != null) base[s.id] = inner.complianceStatus[s.id];
    });
    return base;
  }
  if (Array.isArray(legacyStandards)) {
    legacyStandards.forEach((row, i) => {
      const id = COMPLIANCE_STANDARDS[i]?.id;
      if (id && row?.status) base[id] = row.status === 'na' ? 'pending' : row.status;
    });
  }
  return base;
}

export default function CmExternalQcInspection({ inspection, qaRequest, inspectorAgencyName, inspectorDisplayName, onReload }) {
  const navigate = useNavigate();
  /** Must be declared before any hook that references it (avoids TDZ / "before initialization" in some bundles). */
  const isCompleted = (inspection?.status || '').toLowerCase() === 'completed';

  const [activeTab, setActiveTab] = useState('results');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** Checklist rows loaded from CM QC requirements API (or fallback) */
  const [qcChecklistLoading, setQcChecklistLoading] = useState(true);
  /** Server rejected QC load (e.g. buyer has not approved norms yet) — blocks editing until resolved */
  const [qcLoadBlockedReason, setQcLoadBlockedReason] = useState(null);

  const batchLabel = qaRequest?.qa_request_number || `QA-${qaRequest?.qa_request_id || ''}`;
  const productName =
    qaRequest?.scope_logistics?.product_name || qaRequest?.scope_logistics?.product_description || '—';
  const manufacturerName = qaRequest?.cm_manufacturer_name || '—';
  const orderRef = `ORD-CM-${qaRequest?.cm_rfq_id || qaRequest?.rfq_id || qaRequest?.qa_request_id || ''}`;
  const contactPhone =
    qaRequest?.scope_logistics?.contact_phone || qaRequest?.compliance_commercials?.phone || qaRequest?.compliance_commercials?.contact_phone || '—';
  const contactEmail =
    qaRequest?.scope_logistics?.contact_email || qaRequest?.compliance_commercials?.email || qaRequest?.compliance_commercials?.contact_email || '—';

  const initialFromDb = useMemo(() => parseStoredReport(inspection?.inspection_report), [inspection?.inspection_report]);

  const [agency, setAgency] = useState(() => ({
    name: initialFromDb?.agency?.name || inspectorAgencyName || '',
    inspectorName: initialFromDb?.agency?.inspectorName || inspectorDisplayName || '',
    inspectionDate:
      initialFromDb?.agency?.inspectionDate ||
      (inspection?.inspection_date ? String(inspection.inspection_date).slice(0, 10) : new Date().toISOString().slice(0, 10)),
  }));

  const [checklist, setChecklist] = useState([]);

  const [batchSize, setBatchSize] = useState(() => String(initialFromDb?.compliance?.batch_size ?? initialFromDb?.batch_size ?? ''));
  const [samplesInspected, setSamplesInspected] = useState(() =>
    String(initialFromDb?.compliance?.samples_inspected ?? initialFromDb?.samples_inspected ?? '')
  );
  const [aqlApplied, setAqlApplied] = useState(() =>
    migrateAql(initialFromDb?.compliance?.aqlApplied ?? initialFromDb?.compliance?.aql ?? '2.5')
  );
  const [complianceStatus, setComplianceStatus] = useState(() =>
    migrateComplianceStatus(initialFromDb?.compliance, initialFromDb?.compliance?.standards)
  );
  const [labTestResults, setLabTestResults] = useState(() => initialFromDb?.compliance_lab_notes ?? initialFromDb?.labTestResults ?? '');

  /** Agreed CM QC payload from `GET .../qc-requirements` (checklist + external docs). */
  const [qcPayload, setQcPayload] = useState(null);
  /** Per–external-QC-doc upload path + display name (keys = doc id from `externalQcDocs`) */
  const [certPaths, setCertPaths] = useState({});
  const [certDisplayNames, setCertDisplayNames] = useState({});

  const [uploads, setUploads] = useState(() => ({
    comprehensive_report: initialFromDb?.uploads?.comprehensive_report || '',
    photo_docs: initialFromDb?.uploads?.photo_docs || '',
    measurement_data: initialFromDb?.uploads?.measurement_data || '',
    non_conformance: initialFromDb?.uploads?.non_conformance || '',
  }));

  useEffect(() => {
    setAgency((prev) => ({
      ...prev,
      name: prev.name || inspectorAgencyName || '',
      inspectorName: prev.inspectorName || inspectorDisplayName || '',
    }));
  }, [inspectorAgencyName, inspectorDisplayName]);

  /** Inspection results = agreed CM QC requirements (`inspectionChecklistItems`), merged with saved draft */
  useEffect(() => {
    const inner = parseStoredReport(inspection?.inspection_report);
    const draftResults = Array.isArray(inner?.results) ? inner.results : [];

    async function load() {
      const inspectionCompleted = (inspection?.status || '').toLowerCase() === 'completed';
      const iid = inspection?.inspection_id;
      if (iid == null || Number(iid) <= 0) {
        setQcPayload(null);
        setQcLoadBlockedReason(null);
        const fallback =
          draftResults.length > 0
            ? draftResults.map((r) => ({
                id: r.id || String(Math.random()),
                category: r.category,
                title: r.title,
                criteria: r.criteria,
                status: r.status || 'pending',
                notes: r.notes || '',
              }))
            : checklistToResults(qaRequest?.inspection_checklist) ||
              DEFAULT_CHECKLIST.map((r) => ({ ...r, status: 'pending', notes: '' }));
        setChecklist(fallback);
        setQcChecklistLoading(false);
        return;
      }

      setQcChecklistLoading(true);
      try {
        const res = await CommonService.get(API_URLS.QA.INSPECTION_QC_REQUIREMENTS(iid));
        const envelope = res?.data?.data ?? res?.data;
        const payload = envelope?.qc_payload && typeof envelope.qc_payload === 'object' ? envelope.qc_payload : null;
        setQcLoadBlockedReason(null);
        setQcPayload(payload);
        const base = buildChecklistFromQcPayload(payload);
        const merged = draftResults.length ? mergeDraftChecklist(base, draftResults) : base;
        setChecklist(merged);

        const norms = readQcNormsSampleFields(payload);
        const hasDraftAql =
          (inner?.compliance?.aqlApplied != null && String(inner.compliance.aqlApplied).trim() !== '') ||
          (inner?.compliance?.aql != null && String(inner.compliance.aql).trim() !== '');
        if (!hasDraftAql && norms.aql) {
          setAqlApplied(norms.aql);
        }
      } catch (e) {
        setQcPayload(null);
        const status = e?.response?.status;
        const msg = String(e?.response?.data?.message || e?.message || '').trim();
        const blockingBuyerQc =
          status === 400 &&
          (/approved by the buyer/i.test(msg) ||
            /No QC requirements found/i.test(msg) ||
            /QC norms must be approved/i.test(msg));

        if (blockingBuyerQc && !inspectionCompleted) {
          setQcLoadBlockedReason(msg || 'QC requirements cannot be loaded for this inspection.');
          setChecklist(
            draftResults.length
              ? draftResults.map((r) => ({
                  id: r.id || String(Math.random()),
                  category: r.category,
                  title: r.title,
                  criteria: r.criteria,
                  status: r.status || 'pending',
                  notes: r.notes || '',
                }))
              : []
          );
          toast.error(msg || 'QC requirements are not available.');
        } else {
          setQcLoadBlockedReason(null);
          if (status && status !== 400) {
            console.warn('QC requirements fetch:', status, msg || e?.message);
          }
          if (draftResults.length > 0) {
            setChecklist(
              draftResults.map((r) => ({
                id: r.id || String(Math.random()),
                category: r.category,
                title: r.title,
                criteria: r.criteria,
                status: r.status || 'pending',
                notes: r.notes || '',
              }))
            );
          } else {
            const fromReq = checklistToResults(qaRequest?.inspection_checklist);
            setChecklist(fromReq?.length ? fromReq : DEFAULT_CHECKLIST.map((r) => ({ ...r, status: 'pending', notes: '' })));
          }
          if (status === 400 && !blockingBuyerQc) {
            toast.info('Using QA request checklist — CM QC requirements are unavailable for this job.');
          }
        }
      } finally {
        setQcChecklistLoading(false);
      }
    }

    load();
  }, [inspection?.inspection_id, inspection?.inspection_report, inspection?.status, qaRequest?.inspection_checklist]);

  const certificateSlots = useMemo(() => normalizeExternalQcDocs(qcPayload), [qcPayload]);

  useEffect(() => {
    const inner = parseStoredReport(inspection?.inspection_report);
    if (!inner) return;
    const slots = normalizeExternalQcDocs(qcPayload);
    const { paths, names } = hydrateCertificatesFromInner(inner, slots);
    setCertPaths(paths);
    setCertDisplayNames(names);
  }, [inspection?.inspection_report, qcPayload]);

  const stats = useMemo(() => {
    const total = checklist.length;
    let passed = 0;
    let failed = 0;
    let pending = 0;
    checklist.forEach((c) => {
      if (c.status === 'pass') passed += 1;
      else if (c.status === 'fail') failed += 1;
      else pending += 1;
    });
    const done = passed + failed;
    const pct = total === 0 ? 0 : Math.round((done / total) * 100);
    return { total, passed, failed, pending, pct };
  }, [checklist]);

  function setCheckStatus(id, status) {
    setChecklist((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
  }

  const updateStandard = useCallback((id, status) => {
    setComplianceStatus((prev) => ({ ...prev, [id]: status }));
  }, []);

  const uploadFile = async (file, fileType = 'qa_document') => {
    const user = StorageService.getUser() || {};
    const companyName = user.company_name || user.name || 'QA_Inspector';
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', 'company');
    fd.append('companyName', companyName);
    fd.append('fileType', fileType);
    const res = await CommonService.post('/upload/single', fd);
    if (res && res.success && res.data) {
      return res.data.accessPath || res.data.filePath || '';
    }
    throw new Error(res?.message || 'Upload failed');
  };

  const handleFile = async (key, file) => {
    if (!file) return;
    try {
      const path = await uploadFile(file, 'qa_cm_external');
      setUploads((prev) => ({ ...prev, [key]: path }));
      toast.success('File uploaded');
    } catch (e) {
      toast.error(e?.message || 'Upload failed');
    }
  };

  const handleCertUpload = async (docId, file) => {
    if (!file) return;
    try {
      const path = await uploadFile(file, 'qa_cm_external');
      setCertPaths((prev) => ({ ...prev, [docId]: path }));
      setCertDisplayNames((prev) => ({ ...prev, [docId]: file.name }));
      toast.success('File uploaded');
    } catch (e) {
      toast.error(e?.message || 'Upload failed');
    }
  };

  function buildCertificateUploadsPayload() {
    const out = {};
    certificateSlots.forEach((doc) => {
      const path = certPaths[doc.id];
      const name = certDisplayNames[doc.id];
      if (path || (name && String(name).trim())) {
        out[doc.id] = {
          filePath: path || '',
          fileName: name && String(name).trim() ? String(name).trim() : (path ? path.split('/').pop() : ''),
        };
      }
    });
    return out;
  }

  const buildInspectionReportObject = () => ({
    flow: FLOW_KEY,
    [FLOW_KEY]: {
      agency,
      results: checklist,
      compliance: {
        batch_size: batchSize,
        samples_inspected: samplesInspected,
        aqlApplied,
        complianceStatus,
        lab_notes: labTestResults,
      },
      certificateUploads: buildCertificateUploadsPayload(),
      uploads,
      compliance_lab_notes: labTestResults,
    },
  });

  const deriveOverallRating = () => {
    if (stats.failed > 0) return 'Fair';
    if (stats.pending > 0) return 'Good';
    return 'Excellent';
  };

  const saveDraft = async () => {
    if (!inspection?.inspection_id) return;
    if (qcLoadBlockedReason && !isCompleted) {
      toast.error(qcLoadBlockedReason);
      return;
    }
    setSaving(true);
    try {
      const inspection_report = buildInspectionReportObject();
      await CommonService.put(API_URLS.QA.INSPECTION_DRAFT(inspection.inspection_id), {
        overall_rating: deriveOverallRating(),
        inspection_report,
        inspector_recommendations: labTestResults || null,
      });
      toast.success('Draft saved');
      if (onReload) onReload();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const submitFinal = async () => {
    if (!inspection?.inspection_id) return;
    if (qcLoadBlockedReason && !isCompleted) {
      toast.error(qcLoadBlockedReason);
      return;
    }
    for (const doc of certificateSlots) {
      if (!doc.required) continue;
      const uploaded = certPaths[doc.id] && String(certPaths[doc.id]).trim();
      if (!uploaded) {
        toast.error(`Required document not uploaded: ${doc.name}`);
        setActiveTab('certificates');
        return;
      }
    }
    const requiredUploads = uploads.comprehensive_report && uploads.photo_docs;
    if (!requiredUploads) {
      toast.error('Please upload the Comprehensive Inspection Report and Photo Documentation (required).');
      setActiveTab('uploads');
      return;
    }
    if (stats.pending > 0) {
      const ok = window.confirm(
        'Some inspection checks are still Pending. Submit anyway? You can go back and set Pass/Fail for each item.'
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const inspection_report = buildInspectionReportObject();
      await CommonService.put(API_URLS.QA.INSPECTION_COMPLETE(inspection.inspection_id), {
        overall_rating: deriveOverallRating(),
        inspection_report,
        inspector_recommendations:
          [labTestResults, `External QC summary: ${stats.passed} passed, ${stats.failed} failed, ${stats.pending} pending`]
            .filter(Boolean)
            .join('\n\n') || null,
      });
      toast.success('Inspection report submitted');
      navigate('/auction/qa/dashboard/inspections');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const exportReport = () => {
    const blob = new Blob([JSON.stringify(buildInspectionReportObject(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `external-qc-${batchLabel.replace(/[^a-z0-9-_]/gi, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="cm-insp-page">
      <div className="cm-insp-page__top">
        <button type="button" className="cm-insp-back" onClick={() => navigate('/auction/qa/dashboard/inspections')}>
          <FaArrowLeft aria-hidden />
          Back to inspections
        </button>
        <nav className="cm-insp-breadcrumb" aria-label="Breadcrumb">
          <FaHome aria-hidden className="cm-insp-breadcrumb__icon" />
          <Link to="/auction/qa/dashboard">Home</Link>
          <span className="cm-insp-breadcrumb__sep">/</span>
          <span>Quality Control</span>
          <span className="cm-insp-breadcrumb__sep">/</span>
          <span>External QC Inspection</span>
        </nav>
      </div>

      <header className="cm-insp-header">
        <div>
          <p className="cm-insp-header__crumbs" aria-hidden>
            Home &gt; Quality Control &gt; External QC Inspection
          </p>
          <h1 className="cm-insp-header__title">External QC Inspection — {batchLabel}</h1>
          <p className="cm-insp-header__sub">Third-party quality control inspection</p>
        </div>
      </header>

      <section className="cm-insp-meta-bar" aria-label="Order context">
        <div className="cm-insp-meta-bar__items">
          <span className="cm-insp-meta-bar__item">
            <strong>Order:</strong> {orderRef}
          </span>
          <span className="cm-insp-meta-bar__item">
            <strong>Manufacturer:</strong> {manufacturerName}
          </span>
          <span className="cm-insp-meta-bar__item">
            <strong>Product:</strong> {typeof productName === 'string' ? productName.slice(0, 80) : productName}
          </span>
        </div>
        <div className="cm-insp-meta-bar__contact">
          {contactPhone && contactPhone !== '—' ? <div>{contactPhone}</div> : null}
          {contactEmail && contactEmail.includes('@') ? (
            <div>
              <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
            </div>
          ) : null}
        </div>
      </section>

      {isCompleted ? (
        <div className="cm-insp-banner cm-insp-banner--done" role="status">
          This inspection report has been submitted and is read-only.
        </div>
      ) : null}

      {qcLoadBlockedReason && !isCompleted ? (
        <div className="cm-insp-banner cm-insp-banner--blocked" role="alert">
          {qcLoadBlockedReason}
        </div>
      ) : null}

      <section className="cm-insp-stats" aria-label="Inspection summary">
        <div className="cm-insp-stat">
          <span className="cm-insp-stat__label">Total checks</span>
          <span className="cm-insp-stat__value">{stats.total}</span>
        </div>
        <div className="cm-insp-stat cm-insp-stat--pass">
          <span className="cm-insp-stat__label">Passed</span>
          <span className="cm-insp-stat__value">{stats.passed}</span>
        </div>
        <div className="cm-insp-stat cm-insp-stat--fail">
          <span className="cm-insp-stat__label">Failed</span>
          <span className="cm-insp-stat__value">{stats.failed}</span>
        </div>
        <div className="cm-insp-stat cm-insp-stat--pending">
          <span className="cm-insp-stat__label">Pending</span>
          <span className="cm-insp-stat__value">{stats.pending}</span>
        </div>
        <div className="cm-insp-stat cm-insp-stat--wide">
          <span className="cm-insp-stat__label">Completion</span>
          <div className="cm-insp-progress">
            <div className="cm-insp-progress__bar" style={{ width: `${stats.pct}%` }} />
          </div>
          <span className="cm-insp-stat__pct">{stats.pct}%</span>
        </div>
      </section>

      <section className="cm-insp-agency" aria-label="Agency">
        <div className="cm-insp-agency__grid">
          <label className="cm-insp-field">
            <span className="cm-insp-field__label">Agency name</span>
            <input
              type="text"
              className="cm-insp-input"
              value={agency.name}
              onChange={(e) => setAgency((a) => ({ ...a, name: e.target.value }))}
              readOnly={!!inspectorAgencyName}
              disabled={isCompleted}
              placeholder="e.g. Global Quality Inspections Ltd."
            />
          </label>
          <label className="cm-insp-field">
            <span className="cm-insp-field__label">Inspector name</span>
            <input
              type="text"
              className="cm-insp-input"
              value={agency.inspectorName}
              onChange={(e) => setAgency((a) => ({ ...a, inspectorName: e.target.value }))}
              disabled={isCompleted}
              placeholder="Inspector name"
            />
          </label>
          <label className="cm-insp-field">
            <span className="cm-insp-field__label">Inspection date</span>
            <input
              type="date"
              className="cm-insp-input"
              value={agency.inspectionDate?.slice(0, 10)}
              onChange={(e) => setAgency((a) => ({ ...a, inspectionDate: e.target.value }))}
              disabled={isCompleted}
            />
          </label>
        </div>
      </section>

      <div className="cm-insp-toolbar">
        <div className="cm-insp-tabs" role="tablist">
          {[
            { id: 'results', label: 'Inspection Results' },
            { id: 'compliance', label: 'Compliance & Testing' },
            { id: 'certificates', label: 'Certificates' },
            { id: 'uploads', label: 'Upload Reports' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              className={`cm-insp-tab ${activeTab === t.id ? 'cm-insp-tab--active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="cm-insp-toolbar__actions">
          <button type="button" className="cm-insp-btn cm-insp-btn--outline" onClick={exportReport}>
            <FaDownload aria-hidden /> Export
          </button>
          <button
            type="button"
            className="cm-insp-btn cm-insp-btn--outline"
            onClick={saveDraft}
            disabled={saving || isCompleted || qcChecklistLoading || !!qcLoadBlockedReason}
          >
            <FaSave aria-hidden /> {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            className="cm-insp-btn cm-insp-btn--primary"
            onClick={submitFinal}
            disabled={submitting || isCompleted || qcChecklistLoading || !!qcLoadBlockedReason}
          >
            <FaUpload aria-hidden /> {submitting ? 'Submitting…' : 'Submit inspection report'}
          </button>
        </div>
      </div>

      <div className={`cm-insp-body${activeTab === 'compliance' ? ' cm-insp-body--compliance' : ''}`}>
        {activeTab === 'results' && (
          <div className="cm-insp-panel">
            {qcChecklistLoading ? (
              <p className="cm-insp-panel__req-note" role="status">
                Loading inspection checklist from agreed CM QC requirements…
              </p>
            ) : null}
            {!isCompleted && !qcChecklistLoading ? (
              <p className="cm-insp-panel__req-note">
                <span className="cm-insp-req-star" aria-hidden>*</span> Checklist lines come from the agreed CM QC requirements (
                <strong>Inspection checklist</strong> in the buyer QC package). Mark each line <strong>Pass</strong>, <strong>Fail</strong>, or{' '}
                <strong>Pending</strong>. Add optional notes per line.
              </p>
            ) : null}
            {!qcChecklistLoading &&
              checklist.map((item) => (
              <article key={item.id} className="cm-insp-card">
                <div className="cm-insp-card__head">
                  <span className="cm-insp-card__cat">{item.category}</span>
                  <span
                    className={`cm-insp-badge cm-insp-badge--${
                      item.status === 'pass' ? 'pass' : item.status === 'fail' ? 'fail' : 'pending'
                    }`}
                  >
                    {item.status === 'pass' ? 'Passed' : item.status === 'fail' ? 'Failed' : 'Pending'}
                  </span>
                </div>
                <h3 className="cm-insp-card__title">{item.title}</h3>
                <p className="cm-insp-card__criteria">
                  <strong>Acceptance criteria:</strong> {item.criteria}
                </p>
                <div className="cm-insp-card__actions">
                  <button
                    type="button"
                    className={`cm-insp-result-btn cm-insp-result-btn--pass ${item.status === 'pass' ? 'is-on' : ''}`}
                    disabled={isCompleted}
                    onClick={() => setCheckStatus(item.id, 'pass')}
                  >
                    <FaCheck aria-hidden /> Pass
                  </button>
                  <button
                    type="button"
                    className={`cm-insp-result-btn cm-insp-result-btn--fail ${item.status === 'fail' ? 'is-on' : ''}`}
                    disabled={isCompleted}
                    onClick={() => setCheckStatus(item.id, 'fail')}
                  >
                    <FaTimes aria-hidden /> Fail
                  </button>
                  <button
                    type="button"
                    className={`cm-insp-result-btn cm-insp-result-btn--pending ${item.status === 'pending' ? 'is-on' : ''}`}
                    disabled={isCompleted}
                    onClick={() => setCheckStatus(item.id, 'pending')}
                  >
                    <FaInfoCircle aria-hidden /> Pending
                  </button>
                </div>
                <label className="cm-insp-field" style={{ marginTop: 12 }}>
                  <span className="cm-insp-field__label">Inspector notes (optional)</span>
                  <textarea
                    className="cm-insp-input"
                    style={{ minHeight: 72 }}
                    rows={3}
                    value={item.notes || ''}
                    onChange={(e) =>
                      setChecklist((rows) => rows.map((r) => (r.id === item.id ? { ...r, notes: e.target.value } : r)))
                    }
                    disabled={isCompleted}
                    placeholder="Optional inspection notes and observations…"
                  />
                </label>
              </article>
              ))}
          </div>
        )}

        {activeTab === 'compliance' && (
          <div className="cm-insp-compliance-stack">
            <section className="cm-insp-c-card">
              <h3 className="cm-insp-c-card__title">Sample information</h3>
              <div className="cm-insp-c-sample-row">
                <label className="cm-insp-c-field">
                  <span className="cm-insp-c-field__label">Batch size</span>
                  <input
                    type="text"
                    className="cm-insp-c-input"
                    inputMode="numeric"
                    value={batchSize}
                    onChange={(e) => setBatchSize(e.target.value)}
                    disabled={isCompleted}
                    placeholder="e.g. 5000"
                  />
                </label>
                <label className="cm-insp-c-field">
                  <span className="cm-insp-c-field__label">Samples inspected</span>
                  <input
                    type="text"
                    className="cm-insp-c-input"
                    inputMode="numeric"
                    value={samplesInspected}
                    onChange={(e) => setSamplesInspected(e.target.value)}
                    disabled={isCompleted}
                    placeholder="e.g. 500"
                  />
                </label>
                <label className="cm-insp-c-field">
                  <span className="cm-insp-c-field__label">AQL applied</span>
                  <select
                    className="cm-insp-c-input cm-insp-c-input--select"
                    value={aqlApplied}
                    onChange={(e) => setAqlApplied(e.target.value)}
                    disabled={isCompleted}
                  >
                    {AQL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>

            <section className="cm-insp-c-card">
              <h3 className="cm-insp-c-card__title">Compliance standards tested</h3>
              <div className="cm-insp-c-standards-grid">
                {COMPLIANCE_STANDARDS.map((s) => (
                  <div key={s.id} className="cm-insp-c-standard">
                    <div className="cm-insp-c-standard__text">
                      <div className="cm-insp-c-standard__name">{s.label}</div>
                      <div className="cm-insp-c-standard__sub">{s.sub}</div>
                    </div>
                    <select
                      className="cm-insp-c-input cm-insp-c-input--select cm-insp-c-input--status"
                      value={complianceStatus[s.id] || 'pending'}
                      onChange={(e) => updateStandard(s.id, e.target.value)}
                      disabled={isCompleted}
                    >
                      <option value="pending">Pending</option>
                      <option value="pass">Pass</option>
                      <option value="fail">Fail</option>
                    </select>
                  </div>
                ))}
              </div>
            </section>

            <section className="cm-insp-c-card">
              <h3 className="cm-insp-c-card__title">Lab test results</h3>
              <p className="cm-insp-c-card__hint">Enter detailed laboratory outcomes; use &quot;N/A&quot; if not applicable.</p>
              <textarea
                className="cm-insp-c-lab"
                rows={8}
                value={labTestResults}
                onChange={(e) => setLabTestResults(e.target.value)}
                disabled={isCompleted}
                placeholder="Enter detailed laboratory test results, material analysis, performance testing outcomes, and compliance verification…"
              />
            </section>
          </div>
        )}

        {activeTab === 'certificates' && (
          <div className="cm-insp-panel">
            <div className="cm-insp-cert-head">
              <FaAward className="cm-insp-cert-head__icon" aria-hidden />
              <div>
                <h2 className="cm-insp-cert-head__title">External QC documents</h2>
                <p className="cm-insp-cert-head__desc">
                  Upload the documents listed below as agreed under <strong>External QC Docs</strong> in the CM QC requirements. Use the
                  format(s) specified per row; official certificates should include seal and authorized signatures where applicable.
                </p>
              </div>
            </div>
            {certificateSlots.length === 0 ? (
              <div className="cm-insp-empty-docs" role="status">
                No external QC documents were defined in the agreed QC requirements. Add them in the buyer QC modal (External QC Docs
                tab) or ask the buyer to update the QC package.
              </div>
            ) : (
              <div className="cm-insp-upload-grid">
                {certificateSlots.map((doc, idx) => {
                  const Icon = CERT_ICONS[idx % CERT_ICONS.length];
                  const showName = certDisplayNames[doc.id] || certPaths[doc.id];
                  return (
                    <label key={doc.id} className="cm-insp-drop">
                      <input
                        type="file"
                        accept={acceptForDocFormat(doc.fileFormat)}
                        className="cm-insp-drop__input"
                        disabled={isCompleted}
                        onChange={(e) => handleCertUpload(doc.id, e.target.files?.[0])}
                      />
                      <Icon className="cm-insp-drop__icon" aria-hidden />
                      <div className="cm-insp-drop__title-row">
                        <span className="cm-insp-drop__title">{doc.name}</span>
                        {doc.required ? (
                          <span className="cm-insp-drop__required">Required</span>
                        ) : (
                          <span className="cm-insp-drop__optional">Optional</span>
                        )}
                      </div>
                      <div className="cm-insp-drop__sub">{formatHintForDoc(doc)}</div>
                      {showName ? <div className="cm-insp-drop__file">{showName}</div> : null}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'uploads' && (
          <div className="cm-insp-panel">
            <div className="cm-insp-upload-banner">
              Required: comprehensive report and photo documentation. Optional: measurement data and non-conformance report if defects were found.
            </div>
            <div className="cm-insp-upload-grid">
              <label className="cm-insp-drop">
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  className="cm-insp-drop__input"
                  disabled={isCompleted}
                  onChange={(e) => handleFile('comprehensive_report', e.target.files?.[0])}
                />
                <FaUpload className="cm-insp-drop__icon" aria-hidden />
                <div className="cm-insp-drop__title">
                  Comprehensive inspection report <span className="cm-insp-req-star" aria-hidden>*</span>
                </div>
                <div className="cm-insp-drop__sub">PDF, max 20MB</div>
                {uploads.comprehensive_report ? <div className="cm-insp-drop__file">{uploads.comprehensive_report}</div> : null}
              </label>
              <label className="cm-insp-drop">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="cm-insp-drop__input"
                  disabled={isCompleted}
                  onChange={(e) => handleFile('photo_docs', e.target.files?.[0])}
                />
                <FaCamera className="cm-insp-drop__icon" aria-hidden />
                <div className="cm-insp-drop__title">
                  Photo documentation <span className="cm-insp-req-star" aria-hidden>*</span>
                </div>
                <div className="cm-insp-drop__sub">JPG, PNG — key photos</div>
                {uploads.photo_docs ? <div className="cm-insp-drop__file">{uploads.photo_docs}</div> : null}
              </label>
              <label className="cm-insp-drop">
                <input
                  type="file"
                  accept=".pdf,.csv,.xlsx,application/pdf,text/csv"
                  className="cm-insp-drop__input"
                  disabled={isCompleted}
                  onChange={(e) => handleFile('measurement_data', e.target.files?.[0])}
                />
                <FaFileAlt className="cm-insp-drop__icon" aria-hidden />
                <div className="cm-insp-drop__title">Measurement &amp; test data</div>
                <div className="cm-insp-drop__sub">Excel, CSV, PDF (max 10MB)</div>
                {uploads.measurement_data ? <div className="cm-insp-drop__file">{uploads.measurement_data}</div> : null}
              </label>
              <label className="cm-insp-drop">
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  className="cm-insp-drop__input"
                  disabled={isCompleted}
                  onChange={(e) => handleFile('non_conformance', e.target.files?.[0])}
                />
                <FaExclamationCircle className="cm-insp-drop__icon" aria-hidden />
                <div className="cm-insp-drop__title">Non-conformance report</div>
                <div className="cm-insp-drop__sub">If defects identified — PDF max 10MB</div>
                {uploads.non_conformance ? <div className="cm-insp-drop__file">{uploads.non_conformance}</div> : null}
              </label>
            </div>
          </div>
        )}
      </div>

      <footer className="cm-insp-footer">
        <div className="cm-insp-footer__summary">
          Inspection status: {stats.passed} Passed • {stats.failed} Failed • {stats.pending} Pending
        </div>
        <div className="cm-insp-footer__actions">
          <button type="button" className="cm-insp-btn cm-insp-btn--outline" onClick={() => navigate('/auction/qa/dashboard/inspections')}>
            Cancel
          </button>
          <button
            type="button"
            className="cm-insp-btn cm-insp-btn--outline"
            onClick={saveDraft}
            disabled={saving || isCompleted || qcChecklistLoading || !!qcLoadBlockedReason}
          >
            <FaSave aria-hidden /> {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            className="cm-insp-btn cm-insp-btn--primary cm-insp-btn--lg"
            onClick={submitFinal}
            disabled={submitting || isCompleted || qcChecklistLoading || !!qcLoadBlockedReason}
          >
            <FaUpload aria-hidden /> {submitting ? 'Submitting…' : 'Submit inspection report'}
          </button>
        </div>
      </footer>
    </div>
  );
}
